/* eslint-disable react/jsx-no-bind */
import { useQuery } from '@apollo/client';
import {
  TripMediaDocument,
  TripDetailsForTripEditFormQuery,
  TripMediaQuery,
} from '@website/types/graphqlOperations';
import ErrorPage from '@website/components/utilities/ErrorPage';
import UploadMediaToTransLoadIt from '@website/components/media/UploadMediaToTransLoadIt';
import InfiniteScroll from 'react-infinite-scroller';
import LoadingPage from '../../../utilities/LoadingPage';
import Gallery from './Gallery';
import MediaDownloadAll from './MediaDownloadAll';

type Props = {
  trip: TripDetailsForTripEditFormQuery['tripByURL'];
};

export default function MediaManager({ trip }: Props) {
  const { data, loading, error, fetchMore } = useQuery(TripMediaDocument, {
    variables: {
      tripID: trip.id,
      limit: 200,
    },
    fetchPolicy: 'cache-and-network',
  });

  const nextToken = data?.trip?.media?.nextToken;

  async function loadMoreImages() {
    await fetchMore({
      variables: {
        nextToken,
      },
      // After various attempt trying to use typePolicies, which at a
      // cache level, they introduced various issues that I decided
      // for the time being to fallback on updateQuery, which
      // unfortunately will be deprecated in future and replaced by
      // typePolicies. Hopefully, the issues around polling and
      // pagination will have been fixed by then.
      updateQuery: (
        prev: TripMediaQuery,
        { fetchMoreResult }: { fetchMoreResult: TripMediaQuery },
      ): TripMediaQuery => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          ...prev,
          trip: {
            id: prev.trip.id,
            media: {
              nextToken: fetchMoreResult.trip.media.nextToken,
              items: [
                ...prev.trip.media.items,
                ...fetchMoreResult.trip.media.items,
              ],
            },
          },
        };
      },
    });
  }

  if (error) {
    return <ErrorPage />;
  }

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <>
      <h2 className="mb-2 pb-1 mt-3 h4">Upload your trip images</h2>

      <div className="text-center">
        <UploadMediaToTransLoadIt
          hostID={trip.host.id}
          tripID={trip.id}
          allowMultipleSelect
        />
      </div>
      <h2 className="mt-3 h4">Library</h2>
      {data?.trip.media?.items.length > 0 ? (
        <>
          <InfiniteScroll
            pageStart={1}
            loadMore={loadMoreImages}
            hasMore={!!nextToken}
            loader={<LoadingPage />}
          >
            <Gallery media={data?.trip.media.items} />
          </InfiniteScroll>
          <MediaDownloadAll photos={data?.trip.media.items} />
        </>
      ) : (
        <p>
          <em>No images have been uploaded yet.</em>
        </p>
      )}
    </>
  );
}
